/**
 * Reaction to the backend reporting that the Natlink SSO session behind the
 * member cookie has ended — because the member signed out of another service
 * that shares the same SSO session.
 */

import { buildLoginUrl } from "@/utils/auth";

/**
 * ProblemDetails extension the backend sets on a 401 when the SSO session the
 * member cookie was issued under is over. The same name is used by WeHunt Web's
 * API, so both frontends share one reaction.
 */
export const SESSION_ENDED_CODE = "session_ended";

let hasReacted = false;

/** Clears the once-per-page guard. Tests only. */
export function resetSsoSessionEndedGuard(): void {
  hasReacted = false;
}

/**
 * Sends the browser to the backend's SSO start endpoint, keeping the current
 * page as the return path. In SSO mode that endpoint signs the member in
 * silently as whoever SSO holds now, which is the "come back as the other user"
 * half of what single logout is for.
 *
 * Once per page lifetime: a page typically has several requests in flight, and
 * every one of them would otherwise start its own navigation.
 */
export function handleSsoSessionEnded(
  redirect: (url: string) => void = (url) => window.location.assign(url)
): void {
  if (hasReacted) return;
  hasReacted = true;

  const returnPath = `${window.location.pathname}${window.location.search}`;
  redirect(buildLoginUrl({ returnPath }));
}
